/**
 * ExtractedFiguresStatus — compact one-line summary of matter_figures.
 *
 * Lifted from Stage1.tsx as part of the Stage 0 / Stage 1 split so
 * both stages can render the same figures status without duplicating
 * the polling/loading logic.
 *
 * Surfaced between the Uploaded Files block and the rest of the
 * stage's left rail. Three display states:
 *   - All described:   green dot, "N figures extracted · all described"
 *   - Some missed:     amber dot, "N figures extracted · K not described"
 *   - None described:  amber dot, "N figures stored · not described"
 *                      (e.g. EMF/WMF image streams the vision pass
 *                      can't decode — kept on disk + visible to the
 *                      drawings stage, just not seen by the vision
 *                      LLM)
 *
 * Refetches whenever ``docCount`` changes so a second upload updates
 * the count without the drafter needing to reload the matter. A
 * single failed fetch hides the row silently — this is informational
 * scaffolding and must never displace real stage content.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Image as ImageIcon } from 'lucide-react';

import * as api from '@/api/client';


interface ExtractedFiguresStatusProps {
  matterId: string;
  /** Document count from the parent's known list. The component
   *  refetches whenever this changes (a brand-new upload bumps the
   *  count, which forces a re-poll for newly-extracted figures). */
  docCount: number;
  /** Optional out-of-band refresh trigger — bumping this number
   *  forces a refetch even when ``docCount`` hasn't changed (e.g.
   *  re-running vision on existing docs after toggling the vision
   *  mode). */
  refreshKey?: number;
}


export default function ExtractedFiguresStatus({
  matterId, docCount, refreshKey,
}: ExtractedFiguresStatusProps) {
  const [data, setData] = useState<{ figure_count: number; figures: api.MatterFigure[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!matterId || docCount === 0) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    (async () => {
      try {
        const resp = await api.listMatterFigures(matterId);
        if (!cancelled) setData(resp);
      } catch {
        if (!cancelled) { setFailed(true); setData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [matterId, docCount, refreshKey]);

  if (docCount === 0 || failed) return null;

  if (loading && !data) {
    return (
      <div
        data-testid="figures-status-loading"
        className="border border-zinc-200 rounded-sm bg-white p-3 flex items-center gap-2 text-xs text-zinc-500"
      >
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500">Extracted Figures</h3>
        <Loader2 size={12} className="animate-spin" />
      </div>
    );
  }
  if (!data || data.figure_count === 0) return null;

  const captioned = data.figures.filter(
    (f) => (f.description || '').trim() !== '',
  ).length;
  const notCaptioned = data.figure_count - captioned;

  let dotClass: string;
  let label: string;
  let tooltip: string | undefined;
  let severityClass: string;

  if (notCaptioned === 0) {
    dotClass = 'bg-green-500';
    severityClass = 'text-green-700';
    label = `${data.figure_count} figure${data.figure_count === 1 ? '' : 's'} extracted · all described`;
  } else if (captioned > 0) {
    dotClass = 'bg-amber-500';
    severityClass = 'text-amber-700';
    label = `${data.figure_count} figure${data.figure_count === 1 ? '' : 's'} extracted · ${notCaptioned} not described`;
    tooltip = `${notCaptioned} image(s) could not be described by vision (likely EMF/WMF format). They are stored and available for the drawings stage.`;
  } else {
    dotClass = 'bg-amber-500';
    severityClass = 'text-amber-700';
    label = `${data.figure_count} figure${data.figure_count === 1 ? '' : 's'} stored · not described`;
    tooltip = 'The images are stored and available to the drawings stage, but no description was generated for them. '
      + 'That happens when the image format cannot be read (for example EMF/WMF vector images), or when the vision '
      + 'model is not configured or failed. Check the vision model in Settings if these are ordinary pictures.';
  }

  return (
    <div
      className="border border-zinc-200 rounded-sm bg-white p-3"
      data-testid="figures-status-row"
      title={tooltip}
    >
      <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">
        Extracted Figures
      </h3>
      <div
        data-testid="figures-status-line"
        className="flex items-center gap-2 text-xs text-zinc-700 flex-wrap"
      >
        <span
          className={`inline-block w-2 h-2 rounded-full shrink-0 ${dotClass}`}
          aria-hidden="true"
        />
        <ImageIcon size={12} className="text-zinc-500 shrink-0" />
        <span className={severityClass}>{label}</span>
      </div>
    </div>
  );
}
